import React from "react";

import { InlineError, InlineError$PunishWhileEditing } from "../errors";

type Config<Value, Params> = {
  getInitialText: (params: Params) => string;
  textToValue: (text: string, params: Params) => Value;
  valueToText: (value: Value, params: Params) => string;
};

/** @deprecated - use `useField$Custom` or `useFieldSimple` instead */
function withConfig<Value, Params>(config: Config<Value, Params>) {
  return function useField$Text(params: Params) {
    const [text, setText] = React.useState(config.getInitialText(params));
    const [error, setError] = React.useState<InlineError | null | undefined>(
      undefined
    );
    const [panic, setPanic] = React.useState(false);

    const validate = (text: string) => {
      try {
        config.textToValue(text, params);
        return null;
      } catch (error) {
        if (error instanceof InlineError) {
          return error;
        } else {
          return new InlineError$PunishWhileEditing("Unknown error");
        }
      }
    };

    const getValue = () => {
      try {
        return config.textToValue(text, params);
      } catch {
        return undefined;
      }
    };

    const reset = (initialValue: Value) => {
      setText(config.valueToText(initialValue, params));
      setError(undefined);
      setPanic(false);
    };

    const normalize = (text: string) => {
      try {
        const value = config.textToValue(text, params);
        return config.valueToText(value, params);
      } catch {
        return text;
      }
    };

    const setTextByUser = (text: string) => {
      const error = validate(text);
      setText(text);

      if (panic) {
        setError(error);
      } else if (error == null) {
        setError(undefined);
      } else if (error.options.shouldPunishWhileEditing) {
        setError(error);
        setPanic(true);
      } else {
        setError(undefined);
      }
    };

    const validateAfterEditing = () => {
      const error = validate(text);

      if (panic) {
        setError(error);
        !error && setText(normalize(text));
        !error && setPanic(false);
      } else if (!error) {
        setError(null);
        setPanic(false);
        setText(normalize(text));
      } else if (error.options.shouldPunishAfterEditing) {
        setError(error);
        setPanic(true);
      } else {
        setError(undefined);
        setPanic(false);
      }

      return error;
    };

    const validateBeforeSubmitting = () => {
      const error = validate(text);

      if (panic) {
        setError(error);
        !error && setText(normalize(text));
        !error && setPanic(false);
      } else if (!error) {
        setError(null);
        setPanic(false);
        setText(normalize(text));
      } else {
        setError(error);
        setPanic(true);
      }

      if (error) {
        throw error;
      } else {
        return config.textToValue(text, params);
      }
    };

    return {
      text,
      setText: setTextByUser,
      value: getValue(),
      reset,
      error,
      validateAfterEditing,
      validateBeforeSubmitting,
    };
  };
}

export const useField$Text = {
  withConfig,
};
